import React from 'react';
import {
  Box,
  Typography,
  Grid,
  TypographyProps,
  Container,
  BoxProps,
} from '@mui/material';
import { isMobileScreen } from '../../../helper';

const titleTypoProps: TypographyProps['sx'] = {
  fontSize: '25px',
  fontWeight: 600,
  color: '#1E293B',
  textAlign: 'left',
  fontFamily: 'inherit',
  marginBottom: '10px',
};

const bodyTypoProps: TypographyProps['sx'] = {
  fontSize: '16px',
  fontWeight: 400,
  color: '#94A3B8',
  textAlign: 'left',
  fontFamily: 'inherit',
};

const aiTypoSubProps: TypographyProps = {
  component: 'span',
  fontSize: '25px',
  fontWeight: 600,
  sx: {
    background: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
};

const fadingLineRight: BoxProps['sx'] = {
  borderTop: '3px solid',
  borderImageSource:
    'linear-gradient(270deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0))',
  borderImageSlice: 1,
  width: { xs: '100%', sm: '100%', md: '40%', lg: '40%' },
  ml: 'auto',
};

const fadingLineLeft: BoxProps['sx'] = {
  borderTop: '3px solid',
  borderImageSource: 'linear-gradient(90deg, rgba(0,0,0,0.1), rgba(0,0,0,0))',
  borderImageSlice: 1,
  width: { xs: '100%', sm: '100%', md: '40%', lg: '40%' },
  mr: 'auto',
};

const FeatureDescriptions: React.FC = () => {
  const isMobile = isMobileScreen();
  return (
    <>
      <Box component="section" sx={{ backgroundColor: '#FBFCFD' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" paddingTop="15px">
            <Grid item xs={12} md={6}>
              <Box
                p={2}
                sx={{
                  maxWidth: isMobile ? 'unset' : '500px',
                  mt: isMobile ? 'unset' : '-15px',
                }}
              >
                <Typography variant="h4" sx={titleTypoProps} gutterBottom>
                  <Typography {...aiTypoSubProps}>AI-Powered </Typography>
                  Diagnostic Assessment
                </Typography>
                <Typography sx={bodyTypoProps} gutterBottom>
                  Take a quick quiz to evaluate your understanding of key
                  concepts. Our AI analyzes your performance to pinpoint
                  strengths and areas for improvement. Get a personalized
                  learning path to maximize your progress.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ paddingLeft: isMobile ? 'unset' : '50px' }}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/section1.png`}
                  alt="detail-banner"
                  style={{
                    width: '100%',
                    maxWidth: '520px',
                    height: 'auto',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
        <Box sx={fadingLineRight} />
      </Box>

      <Container component="section" maxWidth="lg">
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box p={2} textAlign="left">
              <img
                src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/section2.png`}
                alt="detail-banner"
                style={{
                  width: '100%',
                  maxWidth: '520px',
                  height: 'auto',
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              p={2}
              sx={{
                maxWidth: isMobile ? 'unset' : '500px',
              }}
            >
              <Typography variant="h4" sx={titleTypoProps} gutterBottom>
                Interactive Resources
              </Typography>
              <Typography sx={bodyTypoProps} gutterBottom>
                Learn with engaging and dynamic study materials tailored to your
                curriculum. Access topic-wise lessons, videos, and practice
                questions designed to make learning effective and enjoyable.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Box component="section" sx={{ backgroundColor: '#FBFCFD' }}>
        <Box sx={fadingLineLeft} />
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" paddingTop="15px">
            <Grid item xs={12} md={6}>
              <Box
                p={2}
                sx={{
                  maxWidth: isMobile ? 'unset' : '500px',
                }}
              >
                <Typography variant="h4" sx={titleTypoProps} gutterBottom>
                  <Typography {...aiTypoSubProps}>AI-Powered </Typography>
                  Smart Marking
                </Typography>
                <Typography sx={bodyTypoProps} gutterBottom>
                  Receive instant, accurate feedback on your answers with our
                  AI-driven marking system. Understand where you went wrong and
                  improve your skills with detailed explanations and scoring
                  insights.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  paddingLeft: isMobile ? 'unset' : '50px',
                  pt: '15px',
                  pb: '15px',
                }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/section3.gif`}
                  alt="detail-banner"
                  style={{
                    width: '100%',
                    maxWidth: '450px',
                    height: 'auto',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
        <Box sx={fadingLineRight} />
      </Box>

      <Container component="section" maxWidth="lg">
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box p={2} textAlign="left">
              <img
                src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/section4.gif`}
                alt="detail-banner"
                style={{
                  width: '100%',
                  maxWidth: '450px',
                  height: 'auto',
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              p={2}
              sx={{
                maxWidth: isMobile ? 'unset' : '500px',
              }}
            >
              <Typography variant="h4" sx={titleTypoProps} gutterBottom>
                Smart Answers - Theory questions
              </Typography>
              <Typography sx={bodyTypoProps} gutterBottom>
                Stuck on a tough question? Our smart answer system provides
                clear, step-by-step explanations to help you grasp complex
                concepts easily. Perfect for self-study and exam preparation.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Box sx={fadingLineLeft} />
    </>
  );
};

export default FeatureDescriptions;
